// src/components/shared/modals/PlaceholderConversionModal.tsx
import { useState, useEffect, useRef, type KeyboardEvent } from 'react';
import { X, Search, Check, AlertCircle } from 'lucide-react';
import { useEscapeKey } from '../../../hooks/useEscapeKey';

interface PlaceholderRequirements {
  department?: string;
  keywords?: string[];
  levelMin?: number | string;
  levelMax?: number | string;
  genEdCategories?: string[];
  [key: string]: unknown;
}

interface PlaceholderForConversion {
  code: string;
  title?: string;
  credits?: number;
  // Optional (#205): AddCourseModal placeholders declare no requirements,
  // and the persisted payload omits what was never stored.
  requirements?: PlaceholderRequirements;
  [key: string]: unknown;
}

export interface PlaceholderConversionPayload {
  placeholder: PlaceholderForConversion;
  fromSemester: string;
  fromIndex: number;
  [key: string]: unknown;
}

export interface ConvertibleCourse {
  code: string;
  title?: string;
  credits?: number;
  description?: string;
  gen_ed_requirements?: string[];
  [key: string]: unknown;
}

export interface PlaceholderConversionModalProps {
  isOpen: boolean;
  onClose: () => void;
  placeholderData: PlaceholderConversionPayload | null | undefined;
  onConvert: (
    placeholderCode: string,
    realCourse: ConvertibleCourse,
    fromSemester: string,
    fromIndex: number
  ) => void;
  canFulfillPlaceholder: (
    course: ConvertibleCourse | { code?: string; [key: string]: unknown },
    placeholder: PlaceholderForConversion | { [key: string]: unknown }
  ) => boolean;
  courses: ConvertibleCourse[];
  onSearch: (query: string) => Promise<ConvertibleCourse[] | null | undefined>;
}

const SEARCH_DEBOUNCE_MS = 300;

const PlaceholderConversionModal = ({
  isOpen,
  onClose,
  placeholderData,
  onConvert,
  canFulfillPlaceholder,
  courses,
  onSearch,
}: PlaceholderConversionModalProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCourses, setFilteredCourses] = useState<ConvertibleCourse[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<ConvertibleCourse | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  // Monotonic sequence so a slow earlier search can't overwrite the results
  // of a later keystroke (search-as-you-type races).
  const searchSeqRef = useRef(0);

  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    if (isOpen && placeholderData) {
      const { placeholder } = placeholderData;
      // requirements is OPTIONAL (#205): AddCourseModal placeholders carry
      // only title/credits/notes, and the persisted payload faithfully
      // omits what was never declared — an unguarded read here crashed the
      // staging rehearsal's conversion.
      const reqs = placeholder.requirements ?? {};

      let query = '';
      if (reqs.department) {
        query = reqs.department;
      }
      if (reqs.keywords && reqs.keywords.length > 0) {
        query += ' ' + reqs.keywords.join(' ');
      }

      // Seeding searchTerm is enough — the debounced search-as-you-type
      // effect below fires for it like any keystroke.
      setSearchTerm(query.trim());
      setSelectedCourse(null);
      setFilteredCourses([]);
      setSearchPerformed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, placeholderData]);

  const handleSearch = async (searchQuery: string = searchTerm): Promise<void> => {
    if (!searchQuery.trim() || !placeholderData) return;
    const seq = ++searchSeqRef.current;

    setLoading(true);
    setSearchPerformed(true);

    try {
      const searchResults = await onSearch(searchQuery);
      if (seq !== searchSeqRef.current) return; // stale response — a newer search superseded it

      if (searchResults) {
        const filtered = searchResults.filter((course) =>
          canFulfillPlaceholder(course, placeholderData.placeholder)
        );
        setFilteredCourses(filtered);
      }
    } catch (error) {
      console.error('Error searching courses:', error);
    } finally {
      if (seq === searchSeqRef.current) {
        setLoading(false);
      }
    }
  };

  // Search-as-you-type (fifth+sixth staging-rehearsal catch): the box only
  // searched on Enter/button, and the open-time auto-search fired only for
  // placeholders that declared a department/keywords — a bare placeholder
  // left a dead search box. Debounced live search; clearing the box resets
  // to the browsable canFulfillPlaceholder list.
  useEffect(() => {
    if (!isOpen) return;
    const query = searchTerm.trim();
    if (!query) {
      searchSeqRef.current += 1; // invalidate any in-flight search
      setFilteredCourses([]);
      setSearchPerformed(false);
      setLoading(false);
      return;
    }
    const timer = setTimeout(() => {
      handleSearch(query);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, isOpen]);

  const handleSelectCourse = (course: ConvertibleCourse) => {
    setSelectedCourse(course);
  };

  const handleConfirmConversion = () => {
    if (selectedCourse && placeholderData) {
      const { placeholder, fromSemester, fromIndex } = placeholderData;
      onConvert(placeholder.code, selectedCourse, fromSemester, fromIndex);
    }
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  if (!isOpen || !placeholderData) return null;

  const { placeholder, fromSemester } = placeholderData;
  // Same optional-requirements guard as the search-prefill effect above.
  const reqs = placeholder.requirements ?? {};

  const matchingCourses: ConvertibleCourse[] = searchPerformed
    ? filteredCourses
    : courses.filter((course) => canFulfillPlaceholder(course, placeholder));

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Convert Placeholder to Course</h2>
            <p className="text-sm text-gray-600 mt-1">
              Replace "{placeholder.title}" with a real course
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-sunken rounded-control transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-slate-900 mb-2">Placeholder Requirements</h3>
            <div className="space-y-1 text-sm text-slate-700">
              {reqs.department && (
                <p>
                  • Department: <strong>{reqs.department}</strong>
                </p>
              )}
              {(reqs.levelMin || reqs.levelMax) && (
                <p>
                  • Level:{' '}
                  <strong>
                    {reqs.levelMin || 'Any'} -{' '}
                    {reqs.levelMax || 'Any'}
                  </strong>
                </p>
              )}
              {reqs.genEdCategories &&
                reqs.genEdCategories.length > 0 && (
                  <p>
                    • Gen Ed:{' '}
                    <strong>{reqs.genEdCategories.join(', ')}</strong>
                  </p>
                )}
              {reqs.keywords &&
                reqs.keywords.length > 0 && (
                  <p>
                    • Keywords: <strong>{reqs.keywords.join(', ')}</strong>
                  </p>
                )}
              <p>
                • Credits: <strong>{placeholder.credits}</strong>
              </p>
            </div>
          </div>

          <div className="mb-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                placeholder="Search for courses..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button
                onClick={() => handleSearch()}
                disabled={loading}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-800 disabled:opacity-50 flex items-center gap-2"
              >
                <Search className="h-4 w-4" />
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Showing courses that match the placeholder requirements
            </p>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <h4 className="font-medium text-gray-700">
                {loading ? 'Searching...' : `${matchingCourses.length} matching courses found`}
              </h4>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500">
                  <Search className="h-8 w-8 mx-auto mb-2 animate-pulse" />
                  <p>Searching for matching courses...</p>
                </div>
              ) : matchingCourses.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>No courses found matching the requirements</p>
                  <p className="text-sm mt-2">Try adjusting your search terms</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {matchingCourses.map((course, index) => (
                    <div
                      key={`${course.code}-${index}`}
                      onClick={() => handleSelectCourse(course)}
                      className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                        selectedCourse?.code === course.code
                          ? 'bg-slate-50 border-l-4 border-slate-500'
                          : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h5 className="font-medium text-gray-900">{course.code}</h5>
                            <span className="text-sm text-gray-500">
                              ({course.credits} credits)
                            </span>
                            {selectedCourse?.code === course.code && (
                              <Check className="h-4 w-4 text-slate-600" />
                            )}
                          </div>
                          <p className="text-sm text-gray-700 mt-1">{course.title}</p>
                          {course.description && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                              {course.description}
                            </p>
                          )}
                          {course.gen_ed_requirements && course.gen_ed_requirements.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {course.gen_ed_requirements.map((req, idx) => (
                                <span
                                  key={idx}
                                  className="text-xs bg-orange-400/15 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full"
                                >
                                  {req}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {selectedCourse && (
            <div className="mt-4 p-4 bg-success/10 border border-success/25 rounded-card">
              <h4 className="font-medium text-green-900 mb-2">Selected Course</h4>
              <p className="text-sm text-green-700">
                <strong>{selectedCourse.code}</strong> - {selectedCourse.title} (
                {selectedCourse.credits} credits)
              </p>
              {fromSemester !== 'bookmarks' && (
                <p className="text-xs text-green-600 mt-1">
                  This course will replace the placeholder in{' '}
                  {fromSemester.replace('-', ' ').toUpperCase()}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmConversion}
            disabled={!selectedCourse}
            className="px-4 py-2 text-white bg-primary rounded-md hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Convert to Course
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlaceholderConversionModal;
